import { useState, useRef, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { ThemeProvider } from '@emotion/react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import translator from '@Components/Utilities/Translator';
import TranslationText from '@Components/Utilities/TranslationText';
import { ThemedBox } from '@Components/MuiWrappers/ThemedBox';
import atoms from '@Components/Utilities/SandhillsAtoms';
import TextField from '@Components/Shared/Controls/TextField';
import { focusErroredField } from '@Components/Utilities/SandhillsUtility';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import Calendar from '@Components/MuiWrappers/Calendar';
import RequestACharterModal from '@Components/Shared/Modals/RequestACharterModal';

const DetailRequestACharterBox = (props) => {
    const theme = useRecoilValue(atoms.muiThemes);
    const inputReferences = useRef({});
    const routes = useRecoilValue(atoms.routes);
    const [success, setSuccess] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const today = new Date();
    const [showRequestACharterModal, setShowRequestACharterModal] = useState(props.ShowRequestACharterModalOnLoad);
    const toggleRequestACharterModal = () => {
        setShowRequestACharterModal(showRequestACharterModal => !showRequestACharterModal);
    }

    const [formData, setFormData] = useState({
        FromDate: props.FromDate || new Date(),
        ToDate: props.ToDate || new Date(new Date().setDate(new Date().getDate() + 1)),
        Comments: props.Comments || ''
    });
    const submitForm = (e) => {
        e.preventDefault();
        let errors = run(formData, [
            ruleRunner('FromDate', translator.translate('sandhills.datefrom'), ValidationRules.required),
            ruleRunner('ToDate', translator.translate('sandhills.dateto'), ValidationRules.required),
        ]);

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);
        setSubmitted(false);
        if (Object.keys(errors).length <= 0) {
            setSuccess(false);
            setDisableSubmit(true);
            onSubmit();
        }
    };
    const onSubmit = (token) => {
        setSubmitted(true);
        setDisableSubmit(false);
        setShowRequestACharterModal(true);
    };

    const onBeginDateChange = (date) => {
        setFormData(previousState => {
            return { ...previousState, FromDate: date };
        });
    };
    const onEndDateChange = (date) => {
        setFormData(previousState => {
            return { ...previousState, ToDate: date };
        });
    };

    const onChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };

    const sx = {
        '& .MuiOutlinedInput-root.Mui-focused': {
            '& > fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.87)'
            }
        },
        backgroundColor: 'white',
        input: {
            fontSize: '14px',
            color: 'rgba(0, 0, 0, 0.87)'
        },
        button: {
            marginRight: '10px'
        },
        svg: {
            width: '15px',
            height: '15px'
        },
        width: '100%',
        height: '37.13px'
    };

    return (
        <ThemeProvider theme={(!theme.isHostedSite && theme.mainMode == "dark") ? theme.darkTheme : theme.lightTheme}>
            <ThemedBox>
                <div className='dealer-contact'>
                    <div className='dealer-contact__header'>
                        <Typography variant='h2' className='dealer-contact__heading' sx={{ fontWeight: 'bold' }}><TranslationText tKey='sandhills.requestacharterquote' /></Typography>
                    </div>
                    <form id='detail-rac-form' className='form' onSubmit={submitForm}>
                        <ValidationErrors errors={validationErrors} />
                        <div className='rac-row-one'>
                            <div className='form-col'>
                                <label htmlFor='FromDate' className='bold'><TranslationText tKey='Sandhills.DateFrom' /> <span className='required red' /></label>
                                <div className='form-el'>
                                    <Calendar
                                        id='FromDate'
                                        name='FromDate'
                                        value={formData.FromDate}
                                        onChange={onBeginDateChange}
                                        inputFormat='MM/dd/yyyy'
                                        minDate={today}
                                        sx={sx}
                                        InputAdornmentProps={{
                                            sx: {
                                                margin: '0px'
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='form-col'>
                                <label className='bold' htmlFor='ToDate'><TranslationText tKey='Sandhills.DateTo' /> <span className='required red' /></label>
                                <div className='form-el'>
                                    <Calendar
                                        id='ToDate'
                                        name='ToDate'
                                        value={formData.ToDate}
                                        onChange={onEndDateChange}
                                        minDate={formData.FromDate}
                                        inputFormat='MM/dd/yyyy'
                                        sx={sx}
                                        InputAdornmentProps={{
                                            sx: {
                                                margin: '0px'
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='form-row-two'>
                            <div className='form-col comments'>
                                <label htmlFor='Comments' className='bold'><TranslationText tKey='sandhills.tradepubwebsite.mvc.comments' /></label>
                                <TextField
                                    id='Comments'
                                    name='Comments'
                                    value={formData.Comments}
                                    type='textarea'
                                    class='text-area-container'
                                    inputClass='text-area'
                                    required={true}
                                    validationErrors={validationErrors.Comments}
                                    ref={(element) => inputReferences.current[`Comments`] = element}
                                    onFieldChanged={onChange}
                                />
                            </div>
                            <div className='form-col submit'>
                                <Button variant="contained" className='button ts-button' type='submit' id='request-a-charter-submit-button-detail' name='request-a-charter-submit-button-detail' disabled={disableSubmit}>{translator.translate('sandhills.tradepubwebsite.mvc.submit')}</Button>
                                {disableSubmit && <LoadingRing backdrop />}
                            </div>
                        </div>
                    </form>
                    {showRequestACharterModal &&
                        <RequestACharterModal
                            {...props.RequestACharter}
                            toggleModal={toggleRequestACharterModal}
                            showRequestAQuoteModal={showRequestACharterModal}
                            Comments={formData.Comments}
                            DepartingDate={formData.FromDate}
                            ReturningDate={formData.ToDate}
                            DirectMessageToCRMID={props.DirectMessageToCRMID}
                            SelectedAircraftPrefType={props.RequestACharter?.SelectedAircraftPrefType}
                        />
                    }
                </div>
            </ThemedBox>
        </ThemeProvider>
    );
};

export default DetailRequestACharterBox;